import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { DESTINATIONS } from "@/data/content";

export default function Viaggi() {
  return (
    <div data-testid="viaggi-page">
      <Seo
        title="Il mio mondo: i viaggi | Laure Hila — Viaggia in Grande"
        description="Per me viaggiare è scoperta, non solo vacanza. Santorini, Dubai, Creta, Tenerife e le destinazioni che mi hanno cambiato il modo di vedere il mondo."
      />

      <section className="pt-32 md:pt-44 pb-14 md:pb-20 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="eyebrow mb-5">Viaggi</p>
          </FadeIn>
          <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
            <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
              Il mio mondo:
            </MaskedLine>
            <MaskedLine delay={0.25} className="text-5xl md:text-7xl italic text-gold">
              i viaggi
            </MaskedLine>
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-stone">
              Per me un viaggio non è mai stato solo una vacanza. È scoperta,
              prospettiva, quella sensazione di aria diversa che ti entra dentro e
              non se ne va più. Ogni destinazione mi ha lasciato qualcosa: qui
              raccolgo i luoghi che fanno parte della mia storia.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-20 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[240px] md:auto-rows-[280px]">
            <Reveal className="md:col-span-7 md:row-span-2">
              <DestinationCard d={DESTINATIONS[0]} big />
            </Reveal>
            <Reveal delay={0.08} className="md:col-span-5">
              <DestinationCard d={DESTINATIONS[1]} />
            </Reveal>
            <Reveal delay={0.14} className="md:col-span-5">
              <DestinationCard d={DESTINATIONS[2]} />
            </Reveal>
            <Reveal delay={0.2} className="md:col-span-5">
              <DestinationCard d={DESTINATIONS[3]} />
            </Reveal>
            <Reveal delay={0.26} className="md:col-span-7">
              <DestinationCard d={DESTINATIONS[4]} />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <blockquote className="font-serif text-3xl md:text-4xl leading-snug text-ink text-balance">
              «Viaggiare non è soltanto andare altrove. È permettere all'altrove di{" "}
              <span className="italic text-gold">entrare dentro di te</span>.»
            </blockquote>
            <Link
              to="/gallery"
              data-testid="viaggi-cta-gallery"
              className="mt-10 inline-flex items-center gap-2 text-gold font-semibold text-sm tracking-wide link-underline group"
            >
              Sfoglia la gallery
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function DestinationCard({ d, big }) {
  return (
    <div
      data-testid={`viaggi-card-${d.name.toLowerCase()}`}
      className="group relative w-full h-full overflow-hidden rounded-xl"
    >
      <img
        src={d.image.url}
        alt={d.image.alt}
        style={d.pos ? { objectPosition: d.pos } : undefined}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent" />
      <div className="absolute bottom-5 left-5 md:bottom-7 md:left-7">
        <p className={`font-serif text-cream ${big ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"}`}>
          {d.name}
        </p>
        <p className="text-xs uppercase tracking-[0.18em] text-cream/70 mt-1">{d.country}</p>
      </div>
    </div>
  );
}
